// @flow
import React, { useMemo, useEffect, useState, useCallback, useContext } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import assoc from 'ramda/es/assoc';
import reduce from 'ramda/es/reduce';
import isEmpty from 'ramda/es/isEmpty';
import test from 'ramda/es/test';
import nest from 'recompose/nest';
import prop from 'ramda/es/prop';
import pick from 'ramda/es/pick';

import RowLayout, { Header } from 'app/common/layouts/RowLayout';
import getNumEnding from 'app/common/lib/getNumEnding';
import { HR, HeaderLvl3 } from 'app/common/layouts/markup';
import PlainCheckbox from 'app/common/ui-next/plain-checkbox';
import PlainInput, { PlainInputPhoneCC } from 'app/common/ui/PlainInput';
import { PlainColumns, PlainColumn } from 'app/common/ui/PlainGrid';
import PlainTextArea from 'app/common/ui/PlainTextArea';
import { getFIO } from 'app/common/lib/names';
import { emailRe } from 'app/common/constants/regex';
import { validatePhoneByISO } from 'app/common/lib/phone';
import CommentString from 'app/common/ui/CommentString';
import { popup } from 'app/common/actions/popup';

import { selectItemsNumber } from 'app/common/components/Shop';
// $FlowFixMe
import * as methodTypes from 'app/common/actions/shop/order/shippingMethodTypes';
// $FlowFixMe
import * as paymentTypes from 'app/common/actions/shop/order/paymentTypes';
import PaymentMethodsApi, { PaymentMethods, type PaymentMethodState } from 'app/common/components/Shop/PaymentMethodsApi';

import selectCurrentDeliveryPoint from './selectCurrentDeliveryPoint';
import SubmitOrder from './SubmitOrder';
import CheckoutButton from './CheckoutButton';
import Summary from '../../Summary';
import Shipping from './Shipping';
import DeliveryPoints from './DeliveryPoints';
import useReceivers from './useReceivers';
import { useShopCartContext } from '../../CartInit';
import styles from './Checkout.module.scss';;


const validate = (name, value, iso) => {
  if (name === 'phone') {
    return validatePhoneByISO(iso, value);
  }
  return test(emailRe, value);
};

const defaultErrorState = {};
const fieldNames4Errors = ['name', 'email', 'phone'];

const errorRequiredStrings = {
  name: 'Заполните обязательное поле',
  email: 'Заполните обязательное поле',
  phone: 'Заполните обязательное поле',
};
const errorFormatedStrings = {
  email: 'Неверный формат email',
  phone: 'Телефон заполнен не полностью',
};

type Props = {
  baseUrl: string,
  push: (path: string) => void,
}

const Checkout = ({ baseUrl, push }: Props) => {
  const dispatch = useDispatch();

  const iso = useSelector(state => state.dealer.country_iso_code, shallowEqual);
  const userUuid = useSelector(state => state.user.shop.user_id, shallowEqual);
  const { total, amountDiscount } = useSelector(state => state.shop.cart, shallowEqual);

  // Количество товаров в корзине
  const productsNumber = useSelector(selectItemsNumber);

  // Заголовокs
  const header = useMemo(() => {
    if (!productsNumber) return 'Корзина';
    const itemsText = getNumEnding(productsNumber, ['товар', 'товара', 'товаров']);
    return `Корзина (${productsNumber} ${itemsText})`;
  }, [productsNumber]);

  // Методы доставки
  const currentShippingMethodId: number = useSelector(
    state => state.shop.order.shipping.currentShippingId,
    shallowEqual,
  );

  // Данные пользователя
  const {
    phone,
    email,
    name,
    loading: loadingUser,
  } = useSelector(state => state.user, shallowEqual);
  const [form, setForm] = useState({ phone, email, name: getFIO(name), comment: '', is_callback: false });
  useEffect(() => {
    setForm(props => ({ ...props, phone, email, name: getFIO(name) }));
  }, [phone, email, name]);

  // Ошибки формы данных пользователя
  const [errors, setErrors] = useState(defaultErrorState);
  const checkErrors = useCallback(() => {
    const currentErrors = reduce((acc, fieldName) => ({
      ...acc,
      // $FlowFixMe
      ...(form[fieldName] ? {} : { [fieldName]: errorRequiredStrings[fieldName] }),
      ...(
        errorFormatedStrings[fieldName] && !validate(fieldName, form[fieldName], iso)
          ? { [fieldName]: errorFormatedStrings[fieldName] }
          : {}
      ),
    }), {}, fieldNames4Errors);
    if (!isEmpty(currentErrors)) {
      setErrors(currentErrors);
      return false;
    }
    setErrors(defaultErrorState);
    return true;
  }, [form, iso]);

  // Изменение данных пользователя
  const handleChangeFormItem = useCallback((e, phoneVal) => {
    const { value, name: fieldName } = e.currentTarget;
    setForm(assoc(fieldName, phoneVal || value));
    setErrors(defaultErrorState);
  }, []);

  // Изменеие поля "Я хочу чтобы мне позвонили..."
  const handleChangeIsCallback = useCallback((e) => {
    const { checked } = e.currentTarget;
    setForm(assoc('is_callback', checked));
  }, []);

  const [, cartActions] = useShopCartContext();

  // Успешное создание заказа
  const handleSuccessCreateOrder = useCallback(async () => {
    // очистка корзины
    await cartActions.newCart();
    dispatch(popup('Ваш заказ успешно отправлен'));
    push('/shop/history');
  }, [dispatch, push, cartActions]);

  const currentShippingMethodType: number = useSelector(state => state.shop.order.shipping.shippingMethodType, shallowEqual);

  // редирект в корзину (первый шаг), если нет товаров
  useEffect(() => {
    if (productsNumber < 1) {
      push(baseUrl);
    }
  }, [productsNumber, push, baseUrl]);

  // id корзины
  const cartId = useSelector(state => state.shop.cart.id, shallowEqual);

  // Методы оплаты
  const {
    content: { data: paymentMethods },
    loading: loadingPaymentMethod,
  }: PaymentMethodState = useContext(PaymentMethods);

  // Выбранный id метода оплаты (пока PAYMENT_TYPE_CARD по умолчанию)
  const currentPaymentMethodId = useMemo(() => {
    const currentPaymentMethod = paymentMethods.find(
      ({ payment_method_type: pmt }) => pmt === paymentTypes.PAYMENT_TYPE_CARD,
    );
    return prop('id', currentPaymentMethod);
  }, [paymentMethods]);

  const { uuid: deliveryPointUuid } = useSelector(selectCurrentDeliveryPoint);

  const receivers = useReceivers(
    userUuid,
    pick(['name', 'email', 'phone'], form),
    deliveryPointUuid,
  );

  // Даннные для запроса создания заказа
  const submitData = useMemo(() => ({
    cart_id: cartId,
    order: {
      user_uuid: userUuid,
      payment_method_id: currentPaymentMethodId,
      shipping_method_id: currentShippingMethodId,
      delivery_point_uuid: deliveryPointUuid,
      name: form.name,
      email: form.email,
      phone: form.phone,
      // postal_code:
      comment: form.comment,
      is_callback: form.is_callback,
      receivers,
    },
  }), [
    cartId,
    currentPaymentMethodId,
    currentShippingMethodId,
    userUuid,
    form,
    deliveryPointUuid,
    receivers,
  ]);

  return (
    <React.Fragment>
      <RowLayout firstOf>
        <Header>{header}</Header>
      </RowLayout>
      <SubmitOrder
        data={submitData}
        onCheckErrors={checkErrors}
        onSuccess={handleSuccessCreateOrder}
      >
        {(handleSubmit, submitting) => (
          <RowLayout flex last>
            <div className={styles.content}>
              <form onSubmit={handleSubmit}>
                <Shipping />
                {currentShippingMethodType === methodTypes.PICKUP
                  && <React.Fragment>
                    <HR />
                    <DeliveryPoints />
                  </React.Fragment>}
                <HR />
                {/* TODO вынести ?? */}
                <HeaderLvl3>{'Данные покупателя'}</HeaderLvl3>
                <PlainColumns>
                  <PlainColumn div={3}>
                    <PlainInput
                      label="Фамилия, имя и отчество"
                      value={form.name}
                      name="name"
                      onChange={handleChangeFormItem}
                      disabled={submitting}
                      loading={loadingUser}
                      isError={errors.name}
                      errorText={errors.name}
                    />
                  </PlainColumn>
                </PlainColumns>
                <PlainColumns className={styles.rowWithComment}>
                  <PlainColumn div={3}>
                    <PlainInput
                      label="Электронная почта"
                      value={form.email}
                      name="email"
                      onChange={handleChangeFormItem}
                      disabled={submitting}
                      loading={loadingUser}
                      isError={errors.email}
                      errorText={errors.email}
                    />
                  </PlainColumn>
                  <PlainColumn div={3}>
                    <PlainInputPhoneCC
                      label="Телефон"
                      value={form.phone}
                      name="phone"
                      onChange={handleChangeFormItem}
                      disabled={submitting}
                      loading={loadingUser}
                      isError={errors.phone}
                      errorText={errors.phone}
                      hideErrorString={!errors.phones}
                    />
                    <CommentString>
                      {'Номер нужен для сообщений о статусе заказа и связи с курьером'}
                    </CommentString>
                  </PlainColumn>
                </PlainColumns>

                <HR />
                <HeaderLvl3>{'Дополнительно'}</HeaderLvl3>
                <PlainColumns>
                  <PlainColumn div={2}>
                    <PlainTextArea
                      label="Комментарий к заказу"
                      placeholder="Комментарий"
                      name="comment"
                      value={form.comment}
                      onChange={handleChangeFormItem}
                      disabled={submitting}
                      loading={loadingUser}
                      hideErrorString
                    />
                    <CommentString>
                      {'Расскажите, как быстрее добраться к вам, укажите код домофона или другую информацию'}
                    </CommentString>

                  </PlainColumn>
                </PlainColumns>
                <button type="submit" className={styles.hiddenSubmit}>{'Оформить'}</button>
              </form>
            </div>

            {productsNumber > 0
              && <Summary
                linkReturn={`${baseUrl}/orderlist`}
                itemsNumber={productsNumber}
                className={styles.summary}
                sum={total}
                amountDiscount={amountDiscount}
                total={total}
              >
                <div className={styles.checkboxes}>
                  <PlainCheckbox
                    value="is_callback"
                    label="Я хочу, чтобы мне позвонили для подтверждения заказа"
                    className={styles.checkbox}
                    name="is_callback"
                    onChange={handleChangeIsCallback}
                    checked={form.is_callback}
                  />
                </div>
                <CheckoutButton
                  className={styles.checkoutButton}
                  // $FlowFixMe
                  onClick={handleSubmit}
                  submitting={submitting}
                  disabled={loadingPaymentMethod || loadingUser}
                />
              </Summary>}
          </RowLayout>
        )}
      </SubmitOrder>
    </React.Fragment>
  );
};

export default nest(PaymentMethodsApi, Checkout);
